import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { UploadSimple, Sparkle, PawPrint, User, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useHyperColor } from "@/context/HyperColorContext";

const GARMENTS = {
  self: [
    { key: "hoodie", label: "Oversized Hoodie" },
    { key: "tee", label: "Sig Series Tee" },
  ],
  pet: [
    { key: "bandana", label: "Reflective Bandana" },
    { key: "dog-hoodie", label: "Dog Hoodie" },
  ],
};

export default function TryOn() {
  const { user } = useAuth();
  const { active } = useHyperColor();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [subject, setSubject] = useState("self");
  const [garment, setGarment] = useState("hoodie");
  const [preview, setPreview] = useState("");
  const [uploadPath, setUploadPath] = useState("");
  const [result, setResult] = useState("");
  const [busy, setBusy] = useState(false);

  const requireAuth = () => {
    if (!user) { toast.error("Sign in to try on Wigglez fitz"); navigate("/auth"); return false; }
    return true;
  };

  const switchSubject = (s) => {
    setSubject(s);
    setGarment(GARMENTS[s][0].key);
    setResult("");
  };

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!requireAuth()) return;
    setPreview(URL.createObjectURL(f));
    setResult("");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploadPath(data.path);
      toast.success("Photo ready");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
      setPreview("");
    } finally { setBusy(false); }
  };

  const generate = async () => {
    if (!requireAuth()) return;
    if (!uploadPath) return toast.error("Upload a photo first");
    setBusy(true);
    try {
      const { data } = await api.post("/tryon/generate", {
        image_path: uploadPath, subject, garment, color: active?.name || "hot-pink and aqua",
      });
      setResult(`${API}/collab-image/${data.art_path}`);
      toast.success("Your fit is on");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14" data-testid="tryon-page">
      <p className="overline text-primary mb-2 flex items-center gap-2"><Sparkle weight="fill" size={14} /> The Vault Try-On · Aura-Sync</p>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] mb-3">
        See yourself in <span className="neon-text">Wigglez fitz</span>.
      </h1>
      <p className="text-muted-foreground max-w-2xl mb-9">
        Upload a photo of you or your pit. Our AI drops the gear right onto your shot — see the fit in your hypercolor before you buy.
      </p>

      {/* Subject switch */}
      <div className="inline-flex p-1 rounded-[12px] border border-white/15 mb-8" data-testid="tryon-subject">
        <button onClick={() => switchSubject("self")} data-testid="tryon-self"
          className={`inline-flex items-center gap-2 overline px-5 py-2.5 rounded-[10px] transition-colors ${subject === "self" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-white"}`}>
          <User weight="fill" size={16} /> Me
        </button>
        <button onClick={() => switchSubject("pet")} data-testid="tryon-pet"
          className={`inline-flex items-center gap-2 overline px-5 py-2.5 rounded-[10px] transition-colors ${subject === "pet" ? "bg-secondary text-secondary-foreground" : "text-muted-foreground hover:text-white"}`}>
          <PawPrint weight="fill" size={16} /> My Pit
        </button>
      </div>

      <div className="grid lg:grid-cols-2 gap-10">
        {/* Controls */}
        <div>
          <p className="overline text-muted-foreground mb-3">1 · Upload a {subject === "self" ? "photo of you" : "photo of your dog"}</p>
          <button onClick={() => fileRef.current?.click()} disabled={busy}
            className="w-full aspect-[4/3] rounded-[12px] border-2 border-dashed border-primary/40 grid place-items-center gap-2 hover:border-primary hover:bg-primary/5 transition-colors disabled:opacity-60"
            data-testid="tryon-upload-btn">
            {preview ? (
              <img src={preview} alt="your upload" className="h-full w-full object-cover rounded-[10px]" />
            ) : (
              <>
                <UploadSimple size={34} className="text-primary" />
                <span className="text-sm text-muted-foreground">{busy ? "Working…" : "Click to upload"}</span>
              </>
            )}
          </button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPick} data-testid="tryon-file-input" />

          <p className="overline text-muted-foreground mt-8 mb-3">2 · Pick your gear</p>
          <div className="grid grid-cols-2 gap-3 mb-8">
            {GARMENTS[subject].map((g) => (
              <button key={g.key} onClick={() => setGarment(g.key)} data-testid={`tryon-garment-${g.key}`}
                className={`inline-flex items-center justify-center gap-2 px-4 py-3 rounded-[10px] border font-modern text-sm transition-colors ${garment === g.key ? "border-primary bg-primary/10 text-white" : "border-white/15 text-muted-foreground hover:border-white/30"}`}>
                <PawPrint weight="fill" size={14} className="text-primary" /> {g.label}
              </button>
            ))}
          </div>

          <button onClick={generate} disabled={busy || !uploadPath}
            className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-7 py-3.5 rounded-[10px] font-modern font-medium neon-glow disabled:opacity-40 hover:bg-primary/90 transition-colors"
            data-testid="tryon-generate">
            <PawPrint weight="fill" size={18} /> {result ? "Re-run fit" : "Try it on"}
          </button>
        </div>

        {/* Preview */}
        <div className="relative">
          <div className="sticky top-24">
            <div className="relative aspect-[4/5] rounded-[12px] overflow-hidden bg-card border border-white/10 neon-glow grid place-items-center" data-testid="tryon-preview">
              {busy && uploadPath && !result ? (
                <div className="text-center" data-testid="tryon-loading">
                  <div className="mx-auto h-12 w-12 rounded-full border-2 border-primary border-t-transparent animate-spin mb-4" />
                  <p className="text-sm text-muted-foreground">Fitting your gear… ~15–30s</p>
                </div>
              ) : result ? (
                <img src={result} alt="virtual fit" className="h-full w-full object-cover" data-testid="tryon-result" />
              ) : (
                <div className="text-center text-muted-foreground">
                  <ArrowsClockwise size={40} className="mx-auto mb-3 text-primary/60" />
                  <p className="text-sm">Your fit preview lands here</p>
                </div>
              )}
            </div>
            {result && (
              <button onClick={() => navigate(subject === "pet" ? "/dog-gear" : "/shop")}
                className="mt-4 inline-flex items-center gap-2 border border-secondary/60 text-secondary px-6 py-3 rounded-[10px] font-modern hover:bg-secondary/10 transition-colors neon-glow-aqua"
                data-testid="tryon-shop">
                <PawPrint weight="fill" size={16} /> Shop this look
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
